import { assertNever } from "@towni/common";
import type React from "react";
import {
    forwardRef,
    type ForwardedRef,
    type HTMLAttributes,
    type ReactNode,
} from "react";
import { cn } from "../utils/cn";

const mainAxisClassMap: Record<NonNullable<RowProps["mainAxis"]>, string> = {
    start: "justify-start",
    center: "justify-center",
    end: "justify-end",
    between: "justify-between",
    around: "justify-around",
    evenly: "justify-evenly",
};

const crossAxisClassMap: Record<NonNullable<RowProps["crossAxis"]>, string> = {
    stretch: "items-stretch",
    center: "items-center",
    start: "items-start",
    end: "items-end",
    baseline: "items-baseline",
};

// Only allow rendering as block-level
// elements that are logical flex containers
// (same as Column)
type RowAs =
    | "div"
    | "section"
    | "article"
    | "aside"
    | "main"
    | "nav"
    | "header"
    | "footer";

type RowElementMap = {
    div: HTMLDivElement;
    section: HTMLElement;
    article: HTMLElement;
    aside: HTMLElement;
    main: HTMLElement;
    nav: HTMLElement;
    header: HTMLElement;
    footer: HTMLElement;
};

type RowProps<T extends RowAs = "div"> = {
    children: ReactNode;
    className?: string;
    /** defaults to: `start` (like the default of regular html/css) */
    mainAxis?: "start" | "center" | "end" | "between" | "around" | "evenly";
    /** defaults to: `stretch` (like the default of regular html/css) */
    crossAxis?: "start" | "center" | "end" | "stretch" | "baseline";
    /** defaults to: `div` */
    as?: T;
} & HTMLAttributes<RowElementMap[T]>;

/**
 * A flexible row layout component that renders a flex container using the specified HTML element.
 *
 * @template T - The HTML element type to render as, constrained by `RowAs`.
 * @param props - The properties for the row component.
 * @param props.children - The content to be rendered inside the row.
 * @param props.className - Additional CSS classes to apply to the row container.
 * @param props.mainAxis - The main axis alignment for flex layout (e.g., "start", "center", "end").
 * @param props.crossAxis - The cross axis alignment for flex layout (e.g., "stretch", "center", "start").
 * @param props.as - The HTML element to render as (e.g., "div", "section", "article", etc.). Defaults to "div".
 * @param ref - A forwarded ref to the underlying DOM element.
 * @returns A React element representing the row container with the specified layout and element type.
 */
const RowComponent = <T extends RowAs>(
    props: RowProps<T>,
    ref: ForwardedRef<RowElementMap[T]> | null,
): React.ReactElement => {
    const {
        children,
        className = "",
        mainAxis = "start",
        crossAxis = "stretch",
        as: renderAs = "div",
        ...rest
    } = props;

    const flexClasses = cn(
        "flex flex-row",
        mainAxisClassMap[mainAxis],
        crossAxisClassMap[crossAxis],
        className,
    );

    if (renderAs === "div") {
        return (
            <div
                ref={ref as ForwardedRef<HTMLDivElement>}
                className={flexClasses}
                {...rest}>
                {children}
            </div>
        );
    }
    switch (renderAs) {
        case "section":
            return (
                <section
                    ref={ref as ForwardedRef<HTMLElement>}
                    className={flexClasses}
                    {...rest}>
                    {children}
                </section>
            );
        case "article":
            return (
                <article
                    ref={ref as ForwardedRef<HTMLElement>}
                    className={flexClasses}
                    {...rest}>
                    {children}
                </article>
            );
        case "aside":
            return (
                <aside
                    ref={ref as ForwardedRef<HTMLElement>}
                    className={flexClasses}
                    {...rest}>
                    {children}
                </aside>
            );
        case "main":
            return (
                <main
                    ref={ref as ForwardedRef<HTMLElement>}
                    className={flexClasses}
                    {...rest}>
                    {children}
                </main>
            );
        case "nav":
            return (
                <nav
                    ref={ref as ForwardedRef<HTMLElement>}
                    className={flexClasses}
                    {...rest}>
                    {children}
                </nav>
            );
        case "header":
            return (
                <header
                    ref={ref as ForwardedRef<HTMLElement>}
                    className={flexClasses}
                    {...rest}>
                    {children}
                </header>
            );
        case "footer":
            return (
                <footer
                    ref={ref as ForwardedRef<HTMLElement>}
                    className={flexClasses}
                    {...rest}>
                    {children}
                </footer>
            );
        case "div":
        default:
            assertNever(renderAs);
    }
};

const Row = forwardRef(RowComponent) as <T extends RowAs = "div">(
    props: RowProps<T> & { ref?: ForwardedRef<RowElementMap[T]> },
) => React.ReactElement;

// @ts-expect-error displayName is not typed on generic forwardRef
Row.displayName = "Row";

export { Row };
export type { RowAs, RowElementMap, RowProps };
